import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { z } from 'zod';
import { CreateAlertConfigSchema, RuleJsonOutputSchema } from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Alerts/schema/AlertBuilderSchema';
import { CreateAlertConfigType, FullAlertConfigType, RuleJsonOutputType } from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Alerts/schema/AlertBuilderSchemaTypes';
import { FullAlertConfigSchema } from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Alerts/schema/EditAlertSchema';
import { useSnackbar } from 'src/utils/Hooks';
import { AlertsListResponseSchema, AlertsListResponseType, ToggleAlertStatusRequestSchema, ToggleAlertStatusRequestType, ToggleAlertStatusResponseType, GetAlertByIdRequestSchema, GetAlertByIdResponseSchema, GetAlertByIdResponseType, AlertUpdateResponseSchema, AlertUpdateResponseType, DeleteAlertRequestSchema, DeleteAlertRequestType, DeleteAlertResponseSchema, DeleteAlertResponseType } from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Alerts/schema/ApiSchema';
import { track } from 'src/utils/mixpanel';
import { useUserEmail, useBeaconClientId } from 'src/utils/Hooks/reduxSelectors';

const CREATE_ALERT_PATH = '/apiAlert/alerts/create';
const UPDATE_ALERT_PATH = '/apiAlert/alerts/update';
const LIST_ALERTS_PATH = '/apiAlert/alerts/alerts-list';
const GET_ALERT_BY_ID_PATH = '/apiAlert/alerts/get';
const TOGGLE_ALERT_STATUS_PATH = '/apiAlert/alerts/alert-status';
const DELETE_ALERT_PATH = '/apiAlert/alerts/delete';
const AI_GENERATE_ALERT_PATH = '/api/alert/generate-alert';

// Query keys for cache management
export const ALERTS_QUERY_KEYS = {
  list: (group: string, product: string) => ['alerts', 'list', group, product] as const,
  byId: (alertId: string) => ['alerts', 'byId', alertId] as const
} as const;

export const useListAlerts = (group: string = 'team', product: string = 'beacon') => {
  return useQuery<AlertsListResponseType>({
    queryKey: ALERTS_QUERY_KEYS.list(group, product),
    queryFn: async () => {
      try {
        const response = await axios.get<AlertsListResponseType>(LIST_ALERTS_PATH, {
          params: { group, product },
          headers: { 'Content-Type': 'application/json' }
        });

        // Validate the response with Zod schema
        const validatedData = AlertsListResponseSchema.parse(response.data);
        return validatedData;
      } catch (error) {
        console.error('❌ Error in useListAlerts queryFn:', error);
        if (error instanceof z.ZodError) {
          console.error('❌ Zod Validation Error:', error.errors);
        }
        throw error;
      }
    }
  });
};

export const useGetAlertById = (alertId: string) => {
  return useQuery<GetAlertByIdResponseType>({
    queryKey: ALERTS_QUERY_KEYS.byId(alertId),
    queryFn: async () => {
      try {
        // Prepare request body following GetAlertByIdRequestSchema
        const requestBody = GetAlertByIdRequestSchema.parse({ alertId });

        const response = await axios.put<GetAlertByIdResponseType>(GET_ALERT_BY_ID_PATH, requestBody, {
          headers: { 'Content-Type': 'application/json' }
        });

        // Validate the response with Zod schema
        const validatedData = GetAlertByIdResponseSchema.parse(response.data);
        return validatedData;
      } catch (error) {
        console.error('❌ Error in useGetAlertById queryFn:', error);
        if (error instanceof z.ZodError) {
          console.error('❌ Zod Validation Error:', error.errors);
        }
        throw error;
      }
    },
    enabled: !!alertId // Only run query if alertId is provided
  });
};

export const useCreateAlert = () => {
  const { showSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const userEmail = useUserEmail();
  const beaconClientId = useBeaconClientId();

  const mutation = useMutation<Awaited<ReturnType<typeof axios.post<FullAlertConfigType>>>, unknown, CreateAlertConfigType>({
    mutationFn: async (payload) => {
      // this will omit the variant field automatically
      const parsed = CreateAlertConfigSchema.parse(payload);
      return await axios.post<FullAlertConfigType>(CREATE_ALERT_PATH, parsed, {
        headers: { 'Content-Type': 'application/json' }
      });
    },
    onSuccess: (response, variables) => {
      // Track alert creation in Mixpanel
      track('Alert Created', {
        userEmail,
        beaconClientId,
        alertName: variables.alertName,
        alertDescription: variables.alertDescription,
        notificationMethods: variables.notifications?.methods,
        notificationEmails: variables.notifications?.config?.email?.recipients?.length || 0
      });

      showSnackbar({ message: "You've successfully created a new alert.", type: 'success' });
      // Invalidate alerts list cache to refresh the data
      queryClient.invalidateQueries({ queryKey: ['alerts', 'list'] });
    },
    onError: () => {
      showSnackbar({ message: 'Failed to create alert.', type: 'error' });
    }
  });

  return {
    createAlert: mutation.mutate,
    createAlertAsync: mutation.mutateAsync,
    isPending: mutation.isPending,
    error: mutation.error,
    isSuccess: mutation.isSuccess,
    data: mutation.data?.data
  } as const;
};

export const useUpdateAlert = () => {
  const { showSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const userEmail = useUserEmail();
  const beaconClientId = useBeaconClientId();

  const mutation = useMutation<Awaited<ReturnType<typeof axios.put<AlertUpdateResponseType>>>, unknown, FullAlertConfigType>({
    mutationFn: async (payload) => {
      // Validate the payload with FullAlertConfigSchema
      const parsed = FullAlertConfigSchema.parse(payload);
      return await axios.put<AlertUpdateResponseType>(UPDATE_ALERT_PATH, parsed, {
        headers: { 'Content-Type': 'application/json' }
      });
    },
    onSuccess: (response, variables) => {
      // Track alert update in Mixpanel
      track('Alert Updated', {
        userEmail,
        beaconClientId,
        alertId: variables.alertId,
        alertName: variables.alertName,
        alertDescription: variables.alertDescription,
        notificationMethods: variables.notifications?.methods,
        notificationEmails: variables.notifications?.config?.email?.recipients?.length || 0
      });

      showSnackbar({ message: "Alert updated successfully.", type: 'success' });
      // Invalidate alerts list cache to refresh the data
      queryClient.invalidateQueries({ queryKey: ['alerts', 'list'] });
      // Invalidate specific alert cache if alertId is available
      if (response.data.newConfig.alertId) {
        queryClient.invalidateQueries({ queryKey: ALERTS_QUERY_KEYS.byId(response.data.newConfig.alertId) });
      }
    },
    onError: () => {
      showSnackbar({ message: 'Failed to update alert.', type: 'error' });
    }
  });

  return {
    updateAlert: mutation.mutate,
    updateAlertAsync: mutation.mutateAsync,
    isPending: mutation.isPending,
    error: mutation.error,
    isSuccess: mutation.isSuccess,
    data: mutation.data?.data
  } as const;
};

export const useToggleAlertStatus = () => {
  const { showSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const mutation = useMutation<Awaited<ReturnType<typeof axios.put<ToggleAlertStatusResponseType>>>, unknown, ToggleAlertStatusRequestType>({
    mutationFn: async (payload) => {
      const parsed = ToggleAlertStatusRequestSchema.parse(payload);
      return await axios.put<ToggleAlertStatusResponseType>(TOGGLE_ALERT_STATUS_PATH, parsed, {
        headers: { 'Content-Type': 'application/json' }
      });
    },
    onSuccess: (response) => {
      const statusText = response.data.enabledFlag ? 'enabled' : 'disabled';
      showSnackbar({
        message: `Alert successfully ${statusText}.`,
        type: 'success'
      });
      // Invalidate alerts list cache to refresh the data
      queryClient.invalidateQueries({ queryKey: ['alerts', 'list'] });
    },
    onError: () => {
      showSnackbar({ message: 'Failed to update alert status.', type: 'error' });
    }
  });

  return {
    toggleAlertStatus: mutation.mutate,
    toggleAlertStatusAsync: mutation.mutateAsync,
    isPending: mutation.isPending,
    error: mutation.error,
    isSuccess: mutation.isSuccess,
    data: mutation.data?.data
  } as const;
};

export const useDeleteAlert = () => {
  const { showSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const userEmail = useUserEmail();
  const beaconClientId = useBeaconClientId();

  const mutation = useMutation<Awaited<ReturnType<typeof axios.post<DeleteAlertResponseType>>>, unknown, DeleteAlertRequestType>({
    mutationFn: async (payload) => {
      const parsed = DeleteAlertRequestSchema.parse(payload);
      return await axios.post<DeleteAlertResponseType>(DELETE_ALERT_PATH, parsed, {
        headers: { 'Content-Type': 'application/json' }
      });
    },
    onSuccess: (response, variables) => {
      // Track alert deletion in Mixpanel
      track('Alert Deleted', {
        userEmail,
        beaconClientId,
        alertId: variables.alertId
      });

      showSnackbar({
        message: response.data.message || 'Alert deleted successfully.',
        type: 'success'
      });
      // Invalidate alerts list cache to refresh the data
      queryClient.invalidateQueries({ queryKey: ['alerts', 'list'] });
      // Invalidate specific alert cache
      if (response.data.alertId) {
        queryClient.invalidateQueries({ queryKey: ALERTS_QUERY_KEYS.byId(response.data.alertId) });
      }
    },
    onError: () => {
      showSnackbar({ message: 'Failed to delete alert.', type: 'error' });
    }
  });

  return {
    deleteAlert: mutation.mutate,
    deleteAlertAsync: mutation.mutateAsync,
    isPending: mutation.isPending,
    error: mutation.error,
    isSuccess: mutation.isSuccess,
    data: mutation.data?.data
  } as const;
};

// Types for AI Generate request and response
interface AIGenerateRequestType {
  user_input: string;
  product: string;
}

interface AIGenerateResponseType {
  success: boolean;
  data?: Partial<RuleJsonOutputType>;
  error?: string;
}

export const useAIGenerateTemplate = () => {
  const { showSnackbar } = useSnackbar();

  const mutation = useMutation<Awaited<ReturnType<typeof axios.post<AIGenerateResponseType>>>, unknown, AIGenerateRequestType>({
    mutationFn: async (payload) => {
      return await axios.post<AIGenerateResponseType>(AI_GENERATE_ALERT_PATH, payload, {
        headers: { 'Content-Type': 'application/json' }
      });
    },
    onSuccess: () => {
      showSnackbar({ message: 'AI alert generated successfully.', type: 'success' });
    },
    onError: () => {
      showSnackbar({ message: 'Failed to generate AI alert.', type: 'error' });
    }
  });

  return {
    aiGenerate: mutation.mutate,
    aiGenerateAsync: mutation.mutateAsync,
    isPending: mutation.isPending,
    error: mutation.error,
    isSuccess: mutation.isSuccess,
    data: mutation.data?.data
  } as const;
};

